import { ComponentProps, memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useField } from 'react-final-form'
import cn from 'classnames'
import { BaseSelect } from 'components/ui/selects/BaseSelect'
import { required } from 'forms/validators'
import { checkFieldError } from 'helpers/form'
import { SetterVoidReturn } from 'helpers/types'
import { useIsMobile } from 'hooks/media'
import { debounce } from 'lodash'
import { CustomerAddLocation } from 'store/customerAdd/types'

import { SelectAddress } from '../SelectAddress/SelectAddress'
import { DEBOUNCE_TIME, MIN_QUERY_LENGTH } from './constants'
import { SelectCompanyInput } from './SelectCompanyInput'
import { SelectCompanyOption } from './SelectCompanyOption'
import { SelectCompanyProps } from './types'

import s from './SelectCompany.module.less'

export type TDefaultInputProps = {
  onChange: SetterVoidReturn<string>
  placeholder: string
  searchValue: string
  autoFocus?: boolean
}

export const SelectCompany = memo(function SelectCompany(props: SelectCompanyProps) {
  const {
    selectedOptions,
    setSelectedOptions,
    setSelectedAddressOption,
    debouncedGetCompany,
    company,
    isCompanyLoading,
    errorCompany,
    setErrorCompany,
    errorAddress,
    setErrorAddress,
    isDadataAddressDisable,
    setIsDadataAddressDisable,
    isLegal,
    isIp,
    searchAddressValue,
    setSearchAddressValue,
    getAddress,
    address,
    isAddressLoading,
    initialValue
  } = props
  const [isDropDownOpen, setDropDownOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(initialValue || '')
  const [daDataSearchValue, setDaDataSearchValue] = useState('')
  const [daDataSearchAddressValue, setDaDataSearchAddressValue] = useState('')
  const { meta } = useField<string>('name', {
    validate: required,
    parse: (value?: string) => value || ''
  })

  const debouncedGetAddress = useMemo(() => debounce(getAddress, DEBOUNCE_TIME), [getAddress])

  const isMobile = useIsMobile()

  useEffect(() => {
    if (address !== null) {
      setIsDadataAddressDisable(false)
    } else {
      setIsDadataAddressDisable(true)
    }
  }, [address]) // eslint-disable-line

  useEffect(() => {
    if (daDataSearchValue && debouncedGetCompany) {
      if (daDataSearchValue.trim().length >= MIN_QUERY_LENGTH) {
        debouncedGetCompany(daDataSearchValue)
      }
    }
  }, [daDataSearchValue, debouncedGetCompany])

  useEffect(() => {
    if (initialValue !== searchValue && company) {
      setDropDownOpen(true)
    }
  }, [company]) // eslint-disable-line

  const handleOpen = useCallback((isDropDownOpen: boolean) => {
    setDropDownOpen(isDropDownOpen)
  }, [])

  const handleChange = (e: Array<CustomerAddLocation['selectedOptions']>) => {
    const newSelectedCustomer = e[0]
    if (!newSelectedCustomer || !newSelectedCustomer.value) {
      return
    }
    setSelectedOptions(newSelectedCustomer)
    setSearchValue(newSelectedCustomer.value)
    setDropDownOpen(false)
    setErrorCompany('')
    setSearchAddressValue('')
    if (newSelectedCustomer.kpp || !newSelectedCustomer.address) {
      return
    }
    debouncedGetAddress(newSelectedCustomer.address)
  }

  const onInputChange = (value: string) => {
    setSearchValue(value)
    setDaDataSearchValue(value)
    setErrorCompany('')

    if (!value) {
      setSelectedOptions(null)
    }
  }

  const content = (
    <div className={s.detailBody}>
      {isLegal && (
        <div className={s.modalRow}>
          <div className={s.modalRowTitle}>Юр. адрес</div>
          <div className={s.modalRowContent}>{(selectedOptions?.address as ReactNode) || '—'}</div>
        </div>
      )}
      {!isDadataAddressDisable && isIp && (
        <>
          <div className={s.searchTitle}>Юр. адрес</div>
          <SelectAddress
            address={address}
            daDataSearchAddressValue={daDataSearchAddressValue}
            debouncedGetAddress={debouncedGetAddress}
            errorAddress={errorAddress}
            initialValue={selectedOptions?.address}
            isAddressLoading={isAddressLoading}
            searchAddressValue={searchAddressValue}
            setDaDataSearchAddressValue={setDaDataSearchAddressValue}
            setErrorAddress={setErrorAddress}
            setSearchAddressValue={setSearchAddressValue}
            setSelectedOption={setSelectedAddressOption}
          />
        </>
      )}
      <div className={s.modalRow}>
        <div className={s.modalRowTitle}>ИНН</div>
        <div className={s.modalRowContent}>{selectedOptions?.inn || '—'}</div>
      </div>
      <div className={s.modalRow}>
        <div className={s.modalRowTitle}>КПП</div>
        <div className={s.modalRowContent}>{selectedOptions?.kpp || '—'}</div>
      </div>
    </div>
  )

  const defaultInputProps = useMemo<TDefaultInputProps>(
    () => ({
      placeholder: '',
      searchValue,
      onChange: onInputChange,
      autoFocus: true
    }),
    [searchValue] // eslint-disable-line
  )

  const classNames = useMemo<ComponentProps<typeof BaseSelect>['classNames']>(
    () => ({
      select: { wrapper: s.wrapper, innerWrapper: cn({ [s.innerWrapper_inactive]: !isDropDownOpen }, s.searchInput) },
      option: { noOptionMessageText: s.noOptionMessageText }
    }),
    [isDropDownOpen]
  )

  return (
    <>
      <div className={s.searchTitle}>Название компании / ИНН</div>
      <BaseSelect
        // @ts-ignore
        CustomOptionComponent={SelectCompanyOption}
        // @ts-ignore
        CustomSelectInput={SelectCompanyInput}
        additionalInputProps={defaultInputProps}
        classNames={classNames}
        error={!!errorCompany || checkFieldError(meta)}
        errorLabel={errorCompany}
        hasIcon={false}
        isLoading={isCompanyLoading}
        isMobile={isMobile}
        isOpen={isDropDownOpen && !!company?.length}
        mobileName="Поиск заявок"
        name="name"
        // @ts-ignore
        onChange={handleChange}
        onSetOpen={handleOpen}
        // TODO убрать
        // @ts-ignore
        options={company || []}
        selectTestId="customers-page--add-new-customer-form--company-name-select"
        showLoadingMessage={false}
        size="lg"
        validate={required}
      />
      {company && selectedOptions && content}
    </>
  )
})
